import { SIDE_TABLE_COLUMN_TYPES, tableSideColumnMapper } from "@loanbossinc/component-library";
import { defaultField, defaultPercentField } from "helpers/row-logic-helper";

import { shouldShowField } from "../loanDetailEditUtil";

import { LOAN_HEDGE_REFERENCES } from "./section-loan-hedge-requirements-constants";
import {
  shouldShowMaxCapStrike,
  shouldShowPricingRequirements,
  shouldShowRequiredHedge,
  shouldShowNotional,
  shouldShowTermMonths,
  shouldShowRatingRequirements,
  shouldShowExtensions,
  shouldShowCollateralAssignment,
  shouldShowLegalOpinion,
  shouldShowExecutionRequirements,
  shouldShowMisc
} from "./section-loan-hedge-requirements-rows-logic";

const DEFAULT_WIDTH = 138;

/*
 * NOTE: This section needs loanTypeId to decide if it is going to show something
 *       so the path is not just to hedgeRequirements
 */

export const rows = {
  requiredHedge: {
    isVisible: data => shouldShowRequiredHedge(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Required Hedge",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.STRING_SUBTEXT,
        map: loan => defaultField(loan, "hedgeRequirements.requiredHedge.value"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: LOAN_HEDGE_REFERENCES.REQUIRED_HEDGE_ID
      }
    ]
  },

  notional: {
    isVisible: data => shouldShowNotional(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Notional",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: loan => defaultField(loan, "hedgeRequirements.notional"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: LOAN_HEDGE_REFERENCES.NOTIONAL
      }
    ]
  },

  termMonths: {
    isVisible: data => shouldShowTermMonths(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Term",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DECIMAL_SUBTEXT,
        map: loan => defaultField(loan, "hedgeRequirements.termMonths"),
        Cell: tableSideColumnMapper,
        suffix: loan => " Months"
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: LOAN_HEDGE_REFERENCES.TERM_MONTHS
      }
    ]
  },

  maxCapStrike: {
    isVisible: loan => shouldShowMaxCapStrike(loan),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Max Cap Strike",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT_SUBTEXT,
        map: loan => defaultPercentField(loan, "hedgeRequirements.maxCapStrike", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: LOAN_HEDGE_REFERENCES.MAX_CAP_STRIKE
      }
    ]
  },

  pricingRequirements: {
    isVisible: data => shouldShowField(data, "hedgeRequirements.pricingRequirements") && shouldShowPricingRequirements(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Pricing Requirements",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: loan => defaultField(loan, "hedgeRequirements.pricingRequirements"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: LOAN_HEDGE_REFERENCES.PRICING_REQUIREMENTS
      }
    ]
  },

  ratingRequirements: {
    isVisible: data => shouldShowRatingRequirements(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Rating Requirements",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: loan => defaultField(loan, "hedgeRequirements.ratingsRequirements"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: LOAN_HEDGE_REFERENCES.RATING_REQUIREMENTS
      }
    ]
  },

  extensions: {
    isVisible: data => shouldShowExtensions(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Extensions",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: loan => defaultField(loan, "hedgeRequirements.extensions"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: LOAN_HEDGE_REFERENCES.EXTENSIONS
      }
    ]
  },

  collateralAssignment: {
    isVisible: data => shouldShowCollateralAssignment(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Collateral Assignment",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: loan => defaultField(loan, "hedgeRequirements.collateralAssignment"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: LOAN_HEDGE_REFERENCES.COLLATERAL_ASSIGNMENT
      }
    ]
  },

  legalOpinion: {
    isVisible: data => shouldShowLegalOpinion(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Legal Opinion",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: loan => defaultField(loan, "hedgeRequirements.legalOpinion"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: LOAN_HEDGE_REFERENCES.LEGAL_OPINION
      }
    ]
  },

  executionRequirements: {
    isVisible: data => shouldShowExecutionRequirements(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Execution Requirements",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: loan => defaultField(loan, "hedgeRequirements.executionRequirements"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: LOAN_HEDGE_REFERENCES.EXECUTION_REQUIRENMENTS
      }
    ]
  },

  misc: {
    isVisible: data => shouldShowMisc(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Misc",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: loan => defaultField(loan, "hedgeRequirements.misc"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: LOAN_HEDGE_REFERENCES.MISC
      }
    ]
  }
};
